import BusinessIcon from "@mui/icons-material/Business";
import HandshakeIcon from "@mui/icons-material/Handshake";
import { DialogActions, DialogContent, InputBase } from "@mui/material";
import {
  Autocomplete,
  Button,
  Checkbox,
  ColorPicker,
  CreatedUpdatedBy,
  Field,
  FieldsContainer,
  Form,
  PageHeader,
  Spacer,
  TextField,
} from "common";
import { ParamsContext } from "common/components/Form/ParamsContext";
import List from "common/components/Inputs/List/List";
import FilterProvider from "components/FilterProvider";
import BookingList from "features/bookings/BookingList";
import CouponList from "features/coupons/CouponList";
import HistoryListDialog from "features/histories/HistoryListDialog";
import useAccess from "features/profile/useAccess";
import I18n from "i18n";
import { PartnerCommissionItem } from "partner-commissions/partnerCommissionApi";
import PartnerCommissionList from "partner-commissions/PartnerCommissionList";
import { createContext } from "react";
import usePartnerForm, {
  UsePartnerFormProps,
  UsePartnerFormReturnType,
} from "./usePartnerForm";
export const PartnerContext = createContext<
  UsePartnerFormReturnType | undefined
>(undefined);
export interface PartnerFormProps extends UsePartnerFormProps {}

export default function PartnerForm({
  id,
  afterSave,
  onClose,
  defaultValues,
}: PartnerFormProps) {
  const { hasPermissions, canEditItem } = useAccess();
  const form = usePartnerForm({
    id,
    afterSave,
    defaultValues,
  });
  const {
    params,
    item,
    options,
    errors,
    loading,
    isDirty,
    isCreate,
    isUpdate,
    getIsDirty,
    getRequiredFields,
    onChange,
    save,
    saving,
    dictionaries,
  } = form;
  const title = isCreate ? I18n.t("Partner.addTitle") : item.name;
  const subtitle = isCreate ? undefined : undefined;
  return (
    <ParamsContext.Provider
      value={{ partner_id: id, partner_group_id: item.partner_group_id }}
    >
      <PartnerContext.Provider value={form}>
        <PageHeader
          avatar={<HandshakeIcon />}
          title={title}
          subtitle={subtitle}
          onClose={onClose}
          loading={loading}
        />
        <DialogContent>
          <Form
            loading={loading}
            item={item}
            options={options}
            errors={errors}
            onChange={onChange}
            translationCategory="Partner.attributes"
            translationPlaceholdersCategory="Partner.placeholders"
            translationHelpersCategory="Partner.helpers"
            isDirty={getIsDirty}
            requiredFields={getRequiredFields()}
            disabled={!canEditItem("partner", item)}
          >
            <FieldsContainer>
              <Field>
                <TextField id="name" />
              </Field>
              <Field>
                <TextField id="email" />
              </Field>
              <Field>
                <TextField id="phone" />
              </Field>
              <Field>
                <TextField id="customer_email" />
              </Field>
              <Field>
                <TextField id="customer_phone" />
              </Field>
              <Field>
                <ColorPicker id="color" />
              </Field>
              <Field>
                <TextField id="coupon_code" />
              </Field>
              {!params?.partner_group_id && (
                <Field>
                  <Autocomplete id="partner_group_id" />
                </Field>
              )}
              <Field>
                <Autocomplete id="account_manager_id" />
              </Field>
              <Field fullWidth>
                <Checkbox id="online" />
                <Checkbox id="active" />
                <Checkbox id="monthly_billing" />
              </Field>
              {isCreate && (
                <Field fullWidth>
                  <List<PartnerCommissionItem>
                    id="partner_commissions"
                    icon={<BusinessIcon />}
                    columns={[
                      {
                        id: "valid_from",
                        title: I18n.t(
                          "PartnerCommission.attributes.valid_from",
                        ),
                        inputComponent: InputBase,
                        inputProps: {
                          type: "date",
                        },
                        headerProps: { width: "140px" },
                      },
                      {
                        id: "program_id",
                        title: I18n.t(
                          "PartnerCommission.attributes.program_id",
                        ),
                        inputComponent: Autocomplete,
                        inputProps: {
                          options: dictionaries.programs,
                          colorAttribute: "program.color",
                          disableClearable: false,
                        },
                        headerProps: {},
                      },
                      {
                        id: "headcount",
                        title: I18n.t("PartnerCommission.attributes.headcount"),
                        inputComponent: InputBase,
                        headerProps: {},
                      },
                      {
                        id: "commission_rate",
                        title: I18n.t(
                          "PartnerCommission.attributes.commission_rate",
                        ),
                        inputComponent: InputBase,
                        headerProps: {},
                      },
                    ]}
                  />
                </Field>
              )}
            </FieldsContainer>
          </Form>
          {isUpdate && (
            <>
              <Spacer h={3} />
              <CouponList
                //id="partner"
                showHeader={true}
                modalProps={{
                  maxWidth: "sm",
                }}
              />
              <Spacer h={3} />
              {hasPermissions(["booking.viewany", "booking.viewown"]) && (
                <PartnerCommissionList
                  //id="partner"
                  //icon={<KeyIcon />}
                  //title={I18n.t("PartnerCommission.title")}
                  showHeader={true}
                  modalProps={{
                    maxWidth: "lg",
                  }} /*showHeader={false} toolbarItems={["title", "refresh", "create"]}  showFooter={false}*/
                />
              )}
              <Spacer h={3} />
              {hasPermissions(["booking.viewany", "booking.viewown"]) && (
                <FilterProvider id="partner.bookings" storageType="session">
                  <BookingList
                    id="partner"
                    //icon={<KeyIcon />}
                    //title={I18n.t("Booking.title")}
                    showHeader={true}
                    modalProps={{
                      maxWidth: "lg",
                    }} /*showHeader={false} toolbarItems={["title", "refresh", "create"]}  showFooter={false}*/
                  />
                </FilterProvider>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {isUpdate && (
            <CreatedUpdatedBy
              objectName="Partner"
              item={item}
              sx={{
                p: 1,
                paddingTop: 0,
                paddingBottom: 0.5,
                marginRight: "auto",
              }}
              renderDialog={
                hasPermissions(["history.viewany", "history.viewown"])
                  ? props => <HistoryListDialog {...props} subtitle={title} />
                  : undefined
              }
            />
          )}
          <Button
            title={I18n.t("App.save")}
            disabled={!isDirty}
            onClick={() => save()}
            loading={saving}
          />
        </DialogActions>
      </PartnerContext.Provider>
    </ParamsContext.Provider>
  );
}
